import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const AlertConfigurationPanel = ({ selectedItems = [], onSaveAlert, className = '' }) => {
  const [alertConfig, setAlertConfig] = useState({
    thresholdType: 'percentage',
    thresholdValue: 10,
    keywords: '',
    emailEnabled: true,
    smsEnabled: false,
    frequency: 'daily',
    isActive: true
  });

  const [activeAlerts, setActiveAlerts] = useState([
    {
      id: 1,
      name: "Caterpillar 336E Underpriced",
      threshold: "15% below market",
      keywords: "caterpillar, 336e",
      frequency: "daily",
      status: "active",
      triggered: 2,
      lastTriggered: new Date(Date.now() - 86400000 * 2)
    },
    {
      id: 2,
      name: "High-Hour Equipment Alert",
      threshold: "20% below book value",
      keywords: "hours > 5000",
      frequency: "weekly",
      status: "active",
      triggered: 0,
      lastTriggered: null
    },
    {
      id: 3,
      name: "Komatsu PC350 Watch",
      threshold: "10% below market",
      keywords: "komatsu, pc350",
      frequency: "daily",
      status: "inactive",
      triggered: 5,
      lastTriggered: new Date(Date.now() - 86400000 * 7)
    }
  ]);

  const thresholdTypeOptions = [
    { value: 'percentage', label: 'Percentage below market' },
    { value: 'fixed', label: 'Fixed dollar amount' },
    { value: 'ratio', label: 'Market to book ratio' }
  ];

  const frequencyOptions = [
    { value: 'realtime', label: 'Real-time' },
    { value: 'daily', label: 'Daily digest' },
    { value: 'weekly', label: 'Weekly summary' },
    { value: 'monthly', label: 'Monthly report' }
  ];

  const handleConfigChange = (field, value) => {
    setAlertConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveAlert = () => {
    const newAlert = {
      id: Date.now(),
      name: `Alert for ${selectedItems?.length} items`,
      threshold: `${alertConfig?.thresholdValue}${alertConfig?.thresholdType === 'percentage' ? '%' : '$'} below ${alertConfig?.thresholdType === 'ratio' ? 'book' : 'market'}`,
      keywords: alertConfig?.keywords,
      frequency: alertConfig?.frequency,
      status: alertConfig?.isActive ? 'active' : 'inactive',
      triggered: 0,
      lastTriggered: null
    };

    setActiveAlerts(prev => [newAlert, ...prev]);
    onSaveAlert(newAlert);
    
    // Reset form
    setAlertConfig({
      thresholdType: 'percentage',
      thresholdValue: 10,
      keywords: '',
      emailEnabled: true,
      smsEnabled: false,
      frequency: 'daily',
      isActive: true
    });
  };

  const toggleAlertStatus = (alertId) => {
    setActiveAlerts(prev => prev?.map(alert => 
      alert?.id === alertId 
        ? { ...alert, status: alert?.status === 'active' ? 'inactive' : 'active' }
        : alert
    ));
  };

  const deleteAlert = (alertId) => {
    setActiveAlerts(prev => prev?.filter(alert => alert?.id !== alertId));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-success';
      case 'inactive': return 'text-text-secondary';
      default: return 'text-text-secondary';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className={`bg-surface border border-border rounded-lg shadow-card ${className}`}>
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-text-primary">Alert Configuration</h3>
        <p className="text-sm text-text-secondary mt-1">
          Set up alerts for underpriced inventory opportunities
        </p>
      </div>
      <div className="p-6 space-y-6">
        {/* New Alert Configuration */}
        <div className="space-y-4">
          <h4 className="font-medium text-text-primary">Create New Alert</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Threshold Type"
              options={thresholdTypeOptions}
              value={alertConfig?.thresholdType}
              onChange={(value) => handleConfigChange('thresholdType', value)}
            />
            
            <Input
              label={`Threshold Value ${alertConfig?.thresholdType === 'percentage' ? '(%)' : alertConfig?.thresholdType === 'fixed' ? '($)' : '(ratio)'}`}
              type="number"
              value={alertConfig?.thresholdValue}
              onChange={(e) => handleConfigChange('thresholdValue', parseFloat(e?.target?.value))}
              placeholder="10"
            />
          </div>

          <Input
            label="Keywords (optional)"
            type="text"
            value={alertConfig?.keywords}
            onChange={(e) => handleConfigChange('keywords', e?.target?.value)}
            placeholder="caterpillar, excavator, 2018"
            description="Comma-separated keywords to filter alerts"
          />

          <Select
            label="Notification Frequency"
            options={frequencyOptions}
            value={alertConfig?.frequency}
            onChange={(value) => handleConfigChange('frequency', value)}
          />

          <div className="space-y-3">
            <label className="text-sm font-medium text-text-primary">Notification Methods</label>
            <div className="space-y-2">
              <Checkbox
                label="Email notifications"
                checked={alertConfig?.emailEnabled}
                onChange={(e) => handleConfigChange('emailEnabled', e?.target?.checked)}
              />
              <Checkbox
                label="SMS notifications"
                checked={alertConfig?.smsEnabled}
                onChange={(e) => handleConfigChange('smsEnabled', e?.target?.checked)}
              />
            </div>
          </div>

          <Checkbox
            label="Activate alert immediately"
            checked={alertConfig?.isActive}
            onChange={(e) => handleConfigChange('isActive', e?.target?.checked)}
          />

          <div className="flex justify-end">
            <Button
              onClick={handleSaveAlert}
              iconName="Plus"
              iconPosition="left"
              disabled={!alertConfig?.thresholdValue}
            >
              Create Alert
            </Button>
          </div>
        </div>

        {/* Active Alerts List */}
        <div className="border-t border-border pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium text-text-primary">Active Alerts</h4>
            <span className="text-sm text-text-secondary">
              {activeAlerts?.filter(a => a?.status === 'active')?.length} active
            </span>
          </div>

          <div className="space-y-3">
            {activeAlerts?.map((alert) => (
              <div key={alert?.id} className="bg-muted p-4 rounded-lg">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h5 className="font-medium text-text-primary">{alert?.name}</h5>
                      <span className={`text-sm capitalize ${getStatusColor(alert?.status)}`}>
                        {alert?.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-text-secondary">
                      <div>
                        <span className="font-medium">Threshold:</span> {alert?.threshold}
                      </div>
                      <div>
                        <span className="font-medium">Frequency:</span> {alert?.frequency}
                      </div>
                      <div>
                        <span className="font-medium">Triggered:</span> {alert?.triggered} times
                      </div>
                    </div>
                    
                    {alert?.keywords && (
                      <div className="text-sm text-text-secondary mt-1">
                        <span className="font-medium">Keywords:</span> {alert?.keywords}
                      </div>
                    )}
                    
                    <div className="text-sm text-text-secondary mt-1">
                      <span className="font-medium">Last triggered:</span> {formatDate(alert?.lastTriggered)}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleAlertStatus(alert?.id)}
                      iconName={alert?.status === 'active' ? 'Pause' : 'Play'}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteAlert(alert?.id)}
                      iconName="Trash2"
                      className="text-error hover:text-error"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {activeAlerts?.length === 0 && (
            <div className="text-center py-8">
              <Icon name="Bell" size={48} className="text-text-secondary mx-auto mb-4" />
              <p className="text-text-secondary">No alerts configured yet</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertConfigurationPanel;